import { Component, OnDestroy, OnInit } from '@angular/core';
import { Contract } from '@common/core/models/contract.model';
import { IBitfApiPagination, IBitfApiSorting, IBitfSortOption } from '@common/interfaces';
import { BitfApiRequestPart } from '@common/libs/bitforce/core/api-call-state/bitf-api-request-part';
import { EApiCallStateNames, EApiRequestPartKeys, EContractStatus, eStoreActions } from '@web/enums';
import { Subscription } from 'rxjs';
import { marker as bitfToTranslate } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiCallStateService, LoaderService, ContractsService, StoreService } from '@web/core/services';
import { tap } from 'rxjs/operators';
import { EStatusType } from '@web/enums/status-type.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'tim-contracts-list',
  templateUrl: './contracts-list.component.html',
  styleUrl: './contracts-list.component.scss',
  standalone: false,
})
export class ContractsListComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private subscriptionApi = new Subscription();
  private paginationRequestPart: BitfApiRequestPart;
  private requestPart: BitfApiRequestPart;
  pagination: IBitfApiPagination;
  eApiRequestPartKeys = EApiRequestPartKeys;
  apiCallStateName = EApiCallStateNames.CONTRACTS;

  public eContractStatus = EContractStatus;
  public eStatusType = EStatusType;
  contracts: Contract[] = [];

  displayedColumns: string[] = [
    'contractId',
    'contractName',
    'businessName',
    'seller',
    'entryDate',
    'expirationWarning',
    'isContracted',
    'isAllAttachmentSigned',
    'isPsAssigned',
    'isInvoiced',
    'state',
    'action',
  ];

  public orderSortings = {
    contractId: {
      key: EApiRequestPartKeys.CONTRACT_ID_SORTING,
      label: bitfToTranslate('CONTRACTS.CONTRACT_ID'),
      value: { property: 'contractId' } as IBitfApiSorting,
    } as IBitfSortOption,
    contractName: {
      key: EApiRequestPartKeys.CONTRACT_NAME_SORTING,
      label: bitfToTranslate('CONTRACTS.CONTRACT_NAME'),
      value: { property: 'contractName' } as IBitfApiSorting,
    } as IBitfSortOption,
    businessName: {
      key: EApiRequestPartKeys.BUSINESS_NAME_SORTING,
      label: bitfToTranslate('CONTRACTS.BUSINESS_NAME'),
      value: { property: 'businessName' } as IBitfApiSorting,
    } as IBitfSortOption,
    seller: {
      key: EApiRequestPartKeys.SELLER_SORTING,
      label: bitfToTranslate('COMMON.LABEL.SELLER'),
      value: { property: 'seller' } as IBitfApiSorting,
    } as IBitfSortOption,
    entryDate: {
      key: EApiRequestPartKeys.CONTRACT_ENTRY_DATE_SORTING,
      label: bitfToTranslate('CONTRACTS.CONTRACT_ENTRY_DATE'),
      value: { property: 'entryDate' } as IBitfApiSorting,
    } as IBitfSortOption,
    state: {
      key: EApiRequestPartKeys.STATE_SORTING,
      label: bitfToTranslate('COMMON.LABEL.STATE'),
      value: { property: 'state' } as IBitfApiSorting,
    } as IBitfSortOption,
  };

  constructor(
    public storeService: StoreService,
    public loaderService: LoaderService,
    private apiCallStateService: ApiCallStateService,
    private contractsService: ContractsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.paginationRequestPart = this.apiCallStateService.getRequestPart(
      this.apiCallStateName,
      EApiRequestPartKeys.PAGINATION
    );

    this.subscription.add(
      this.storeService.selectStore(eStoreActions.SET_GROUP).subscribe(() => {
        this.loadData();
      })
    );
    this.subscription.add(
      this.apiCallStateService
        .getStateStore$(this.apiCallStateName)
        .pipe(tap(() => this.loadData()))
        .subscribe()
    );
  }

  private loadData() {
    this.loaderService.show();
    const apiRequest = this.apiCallStateService.getApiRequest(this.apiCallStateName);

    this.subscriptionApi.unsubscribe();
    this.subscriptionApi = new Subscription();
    this.subscriptionApi.add(
      this.contractsService.getContracts(apiRequest).subscribe(response => {
        this.contracts = response.content;
        this.pagination = response.pagination;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptionApi.unsubscribe();
  }

  getExpirationTooltip(contract: Contract): string {
    if (contract.showExpirationEndWarning) {
      return this.translateService.instant('CONTRACTS.EXPIRED_CONTRACT');
    } else if (contract.showExpirationWarning) {
      const daysRemaining = contract.daysUntilExpiration;
      return this.translateService.instant('CONTRACTS.EXPIRING_CONTRACT', { days: daysRemaining });
    }
    return '';
  }
}
